import Medusa from "@medusajs/js-sdk";
import { mergeResources } from "./core/merge.js";
import { createAdminExtensions, createCheckoutSessionsExtensions, createHandoffExtensions, createHooksExtensions, createProductFeedExtensions, createStoreExtensions, createVendorExtensions, } from "./generated/index.js";
/**
 * The Opticomm SDK.
 *
 * Extends `@medusajs/js-sdk`'s `Medusa` client, so everything upstream ships —
 * `store`, `admin`, `auth`, `client`, locale handling — works unchanged, with
 * Opticomm's custom `/store` and `/admin` routes merged onto the matching
 * namespaces and its root-level routes exposed as their own.
 *
 * Every Opticomm method is a thin `client.fetch` call, which is the extension
 * point Medusa documents for custom routes. Base URL, publishable key, API key,
 * JWT/session headers, query encoding, and `FetchError` on non-2xx are all
 * handled upstream and deliberately not reimplemented here.
 *
 * The `admin`, `vendor` and `hooks` namespaces cover back-office routes. They
 * are typed here for convenience, not gated: every one of them is enforced
 * server-side by the actor its route requires, so holding a method on this
 * client grants nothing the caller's token does not already allow.
 *
 * @example
 * ```ts
 * const sdk = new Opticomm({
 *   baseUrl: "https://api.example.com",
 *   publishableKey: "pk_...",
 * })
 *
 * const { moq_rules } = await sdk.store.moqRules.list({ product_id: "prod_123" })
 * const { products } = await sdk.store.product.list()   // upstream Medusa
 *
 * // Back office. Vendors authenticate as the `seller` actor, not `vendor`.
 * await sdk.auth.login("seller", "emailpass", { email, password })
 * const orders = await sdk.vendor.orders.list()
 * ```
 */
export class Opticomm extends Medusa {
    constructor(config) {
        super(config);
        mergeResources(this.store, createStoreExtensions(this.client));
        mergeResources(this.admin, createAdminExtensions(this.client));
        this.checkoutSessions = createCheckoutSessionsExtensions(this.client);
        this.handoff = createHandoffExtensions(this.client);
        this.productFeed = createProductFeedExtensions(this.client);
        this.vendor = createVendorExtensions(this.client);
        this.hooks = createHooksExtensions(this.client);
    }
}
//# sourceMappingURL=opticomm.js.map